import React, { useState, useEffect } from 'react';
import { Book } from '@/utils/bookRouting';
import { useTheme } from '@/contexts/ThemeContext';
import { formatTimeLeft } from '@/utils/timeUtils';
import { getAudioProgressForBook, getEffectiveProgress, saveBookFavourite, saveBookHidden } from '@/utils/audioProgressUtils';
import { bookAnalytics } from '@/services/bookAnalytics';
import TopNavigation from '../components/TopNavigation';
import FavouriteIcon from '../components/icons/FavouriteIcon';
import PlayIcon from '../components/icons/PlayIcon';
import PauseIcon from '../components/icons/PauseIcon';
import EditorialStatement from '../components/EditorialStatement';
import AnimatedBookCover from '../components/AnimatedBookCover';
import type { VoiceEndContext, VoiceStartContext } from '@/services/analyticsContext';

interface BookPageProps {
  book?: Book | null;
  booksLoading?: boolean;
  onStartReading?: (bookSlug: string) => void;
  onNavigateToExplore?: () => void;
  onNavigateToLibrary?: () => void;
  onNavigateToProfile?: () => void;
  onNavigateToMyBooks?: () => void;
  onNavigateHome?: () => void;
  onNavigateToLogin?: (source?: string) => void;
  onNavigateToSignup?: (source?: string) => void;
  onToggleMenu: () => void;
  currentTime?: number;
  onBack?: () => void;
  onConnect?: (context: VoiceStartContext) => Promise<boolean>;
  onDisconnect?: (context: VoiceEndContext) => Promise<void>;
  isConnected?: boolean;
  connectionStatus?: string;
  isBuffering?: boolean;
  isReady?: boolean;
  isPositionRestored?: boolean;
  duration?: number;
  skipForward?: (seconds: number) => void;
  skipBackward?: (seconds: number) => void;
  isLoggedIn?: boolean;
  isMobile?: boolean;
  isLoading?: boolean;
  isPlaying?: boolean;
  onTogglePlay?: () => void;
  isCurrentBook?: boolean;
}

const BookPage: React.FC<BookPageProps> = ({ book, booksLoading = false, onStartReading, onNavigateToExplore, onNavigateToLibrary, onNavigateToProfile, onNavigateToMyBooks, onNavigateHome, onNavigateToLogin, onNavigateToSignup, onToggleMenu, currentTime = 0, isReady = false, isPositionRestored = false, duration = 0, isLoggedIn = false, isMobile = false, isLoading = false, isPlaying = false, onTogglePlay, isCurrentBook = false }) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const [isFavourite, setIsFavourite] = useState(false);
  const [autoPlayCoverVideo, setAutoPlayCoverVideo] = useState(window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => {
      setAutoPlayCoverVideo(window.innerWidth < 1024);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Progress and the favourite flag are read from the local store, which
  // hydration refills from the server after this page may already have
  // rendered. Re-read on the synced event, as My Books does, so both reflect
  // the server's copy rather than whatever the store held at first render.
  const [progressSyncTick, setProgressSyncTick] = useState(0);
  useEffect(() => {
    const handleProgressSynced = () => setProgressSyncTick(tick => tick + 1);
    window.addEventListener('aleph:progress-synced', handleProgressSynced);
    return () => window.removeEventListener('aleph:progress-synced', handleProgressSynced);
  }, []);

  useEffect(() => {
    if (book) {
      const savedPosition = getAudioProgressForBook(book);
      setIsFavourite(!!savedPosition?.isFavourite);
    }
  }, [book, progressSyncTick]);

  if (!book) {
    const themeClasses = isDarkMode ? 'text-theme-primary-dark' : 'bg-theme-light text-theme-primary-light';
    return (
      <div className={`w-full ${themeClasses} transition-colors duration-300`} style={{ backgroundColor: isDarkMode ? 'var(--background-colour-dark-mode)' : 'var(--background-colour-light-mode)' }}>
      <TopNavigation
        isDarkMode={isDarkMode}
        onToggleMenu={onToggleMenu}
        onNavigateHome={onNavigateHome}
        onNavigateToExplore={onNavigateToExplore}
        onNavigateToLogin={(source) => onNavigateToLogin?.(source)}
        onNavigateToSignup={(source) => onNavigateToSignup?.(source)}
        onNavigateToLibrary={onNavigateToLibrary}
        onNavigateToMyBooks={onNavigateToMyBooks}
        onNavigateToProfile={onNavigateToProfile}
        showProfileIcon={isLoggedIn}
        isLoading={isLoading}
      />
        <div className="w-full h-full flex items-center justify-center p-8">
          <h1 className="text-2xl">{booksLoading ? 'Loading...' : 'Book not found'}</h1>
        </div>
      </div>
    );
  }

  const ensureBookUnhidden = () => {
    if (book) {
      const savedPosition = getAudioProgressForBook(book);
      if (savedPosition?.isHidden) {
        saveBookHidden(book.id, false);
        bookAnalytics.trackVisibilityChange(book, {
          hidden: false,
          surface: 'book_page',
          trigger: 'playback_start',
          input_method: 'automatic',
        });
      }
    }
  };

  const handleStartReading = () => {
    ensureBookUnhidden();
    if (onStartReading) {
      onStartReading(book.slug);
    }
  };

  const handleFavourite = () => {
    if (book) {
      const newValue = !isFavourite;
      setIsFavourite(newValue);
      saveBookFavourite(book.id, newValue);
      bookAnalytics.trackFavouriteStatusChange(book, {
        favourited: newValue,
        surface: 'book_page',
        trigger: 'button',
        input_method: 'unknown',
      });
    }
  };

  const themeClasses = isDarkMode ? 'text-theme-primary-dark' : 'bg-theme-light text-theme-primary-light';
  const totalSeconds = Math.max(0, Math.round(book.totalDuration));
  const durationHours = Math.floor(totalSeconds / 3600);
  const durationMinutes = Math.round((totalSeconds % 3600) / 60);
  const durationLabel = durationHours > 0
    ? `${durationHours} hr${durationHours === 1 ? '' : 's'}${durationMinutes > 0 ? ` ${durationMinutes} min` : ''}`
    : `${durationMinutes} min`;

  return (
    <div className={`w-full ${themeClasses} transition-colors duration-300`} style={{ backgroundColor: isDarkMode ? 'var(--background-colour-dark-mode)' : 'var(--background-colour-light-mode)' }}>
      <TopNavigation
        isDarkMode={isDarkMode}
        onToggleMenu={onToggleMenu}
        onNavigateHome={onNavigateHome}
        onNavigateToExplore={onNavigateToExplore}
        onNavigateToLibrary={onNavigateToLibrary}
        onNavigateToMyBooks={onNavigateToMyBooks}
        onNavigateToProfile={onNavigateToProfile}
        onNavigateToLogin={(source) => onNavigateToLogin?.(source)}
        onNavigateToSignup={(source) => onNavigateToSignup?.(source)}
        showProfileIcon={isLoggedIn}
        isLoading={isLoading}
      />

      {/* Main Content - Full Width Responsive with Footer Padding */}
      <div className="w-full flex flex-col px-8 lg:px-16 xl:px-24 py-8 mb-[calc(10rem+var(--safe-bottom))]">
        <div className="w-full max-w-7xl mx-auto">
          {/* Book Cover and Details */}
          <div className="flex flex-col lg:flex-row items-start justify-center gap-16 mb-6">
            {/* Book Cover */}
            <div className="shrink-0 lg:w-96 flex items-center justify-center">
              <div className="text-center">
                <AnimatedBookCover
                  coverImageUrl={book.coverUrl || ''}
                  coverAnimationUrl={book.coverAnimationUrl}
                  title={book.title}
                  author={book.author}
                  containerClassName="mb-6 aspect-[3/4] relative"
                  imageClassName="h-auto mx-auto shadow-2xl"
                  videoClassName="h-auto mx-auto shadow-2xl absolute top-0 left-1/2 transform -translate-x-1/2"
                  imageStyle={{
                    width: '380px',
                    boxShadow: '0 12px 36px rgba(0,0,0,0.45)'
                  }}
                  videoStyle={{
                    width: '380px',
                    boxShadow: '0 12px 36px rgba(0,0,0,0.45)'
                  }}
                  autoPlayOnLoad={autoPlayCoverVideo}
                />
              </div>
            </div>

            {/* Book Details */}
            <div className="flex-1 max-w-2xl lg:max-w-3xl">
              <header>
                <h1 className={`portrait-text-app text-4xl text-left mb-2 ${isDarkMode ? 'text-theme-primary-dark embossed-text-dark' : 'text-theme-primary-light embossed-text'}`}>
                  {book.title}
                </h1>
                {book.subheading && (
                  <h2 className={`portrait-text-app text-3xl text-left mb-2 ${isDarkMode ? 'text-theme-secondary-dark embossed-text-dark' : 'text-theme-secondary-light embossed-text'}`}>
                    {book.subheading}
                  </h2>
                )}
                <p className={`text-lg text-left mb-4 ${isDarkMode ? 'text-theme-secondary-dark' : 'text-theme-secondary-light'}`}>
                  by {book.author}
                </p>
                <p className={`text-sm text-left mb-6 uppercase tracking-[0.08em] ${isDarkMode ? 'text-theme-secondary-dark opacity-80' : 'text-theme-secondary-light opacity-80'}`}>
                  {book.releaseYear ?? 'Unknown year'}
                </p>
              </header>
              
              <div className={`text-sm text-left mb-6 ${isDarkMode ? 'text-black-400' : 'text-black-500'}`}>
                {durationLabel}
              </div>

              {/* Description */}
              {book.description && (
                <div className="mb-6">
                  <p className={`text-sm leading-relaxed text-left ${isDarkMode ? 'text-theme-secondary-dark' : 'text-black-700'}`}>
                    {book.description}
                  </p>
                </div>
              )}

              {/* Progress Bar */}
              <div className="mb-6 w-full sm:w-60">
                  {(() => {
                    if (!book) return null;
                    
                    // Use currentTime if it's greater than 0 (audio is playing), otherwise use saved progress
                    const savedPosition = getAudioProgressForBook(book);
                    const bookCurrentTime = getEffectiveProgress(savedPosition);
                    
                    // Only use currentTime if the player is ready AND position has been restored, otherwise fallback to saved progress to avoid jumps during initialization
                    const effectiveCurrentTime = (currentTime > 0 && isReady && isPositionRestored) ? currentTime : bookCurrentTime;
                    
                    // Use centralized calculation logic where possible, but here we need dynamic updates based on player state
                    const progress = book.totalDuration > 0 ? Math.min(100, Math.max(0, (effectiveCurrentTime / book.totalDuration) * 100)) : 0;
                    const timeLeft = Math.max(0, book.totalDuration - effectiveCurrentTime);

                    return (
                      <>
                        <div className={`w-full h-2 rounded-full overflow-hidden ${
                          isDarkMode ? 'bg-black-700' : 'bg-blue-200'
                        }`}>
                          <div
                            className="h-2 w-full origin-left bg-blue-600 transition-transform duration-300 ease-linear"
                            style={{ transform: `scaleX(${progress / 100})` }}
                          ></div>
                        </div>
                        <div className="flex justify-between items-center mb-1 mt-1">
                          <span className={`text-xs ${isDarkMode ? 'text-black-400' : 'text-black-500'}`}>
                            {timeLeft < 2 ? '100% complete' : `${Math.floor(progress)}% complete`}
                          </span>
                        </div>
                      </>
                    );
                  })()}
                </div>

              {/* Action Buttons */}
              <div className="flex flex-row gap-3 sm:gap-6 items-center justify-start w-full">

                {/* Start Reading Button */}
                <button
                  onClick={() => {
                    if (isCurrentBook && onTogglePlay) {
                      if (!isPlaying) {
                        ensureBookUnhidden();
                      }
                      onTogglePlay();
                    } else {
                      handleStartReading();
                    }
                  }}
                  className={`flex-1 min-w-0 sm:flex-none sm:min-w-30 px-5.5 py-3 rounded-full font-regular text-base transition-all duration-200 shadow-sm hover:shadow-md cursor-pointer border flex items-center justify-center gap-2 ${
                    isDarkMode
                      ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700 hover:border-blue-700'
                      : 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700 hover:border-blue-700'
                  }`}
                >
                  {isCurrentBook && isPlaying ? (
                    <>
                      <PauseIcon size="xs" />
                      <span>Pause</span>
                    </>
                  ) : (
                    <>
                      <PlayIcon size="xs" />
                      <span>Play</span>
                    </>
                  )}
                </button>

                {/* Favourite Button */}
                <button
                  onClick={handleFavourite}
                  className={`flex items-center justify-center w-13 h-13 shrink-0 rounded-full cursor-pointer transition-colors ${
                    isFavourite
                      ? isDarkMode
                        ? 'bg-black-700 text-black-300 hover:bg-black-600'
                        : 'bg-black-100 text-black-700 hover:bg-black-200'
                      : isDarkMode
                        ? 'bg-black-700 text-black-300 hover:bg-black-600'
                        : 'bg-black-100 text-black-700 hover:bg-black-200'
                  }`}
                  title={isFavourite ? 'Remove from Favourites' : 'Add to Favourites'}
                >
                  <FavouriteIcon
                    isFavourite={isFavourite}
                    size="md"
                    className={'mt-0.5'}
                    colorOutlined={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
                    colorFilled={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
                  />
                </button>
              </div>


              
              {/* Editorial Statement Section */}
              <EditorialStatement
                expertReviews={book.expertReviews || []}
                isDarkMode={isDarkMode}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookPage;
